import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useCurrentSPILogic } from 'components/widgets/CurrentSPI/CurrentSPI.logic';
import classes from 'components/widgets/CurrentSPI/CurrentSPI.module.scss';
import { useCurrentSPIProvider } from 'components/widgets/CurrentSPI/CurrentSPI.provider';

const { componentKey, id, title: defaultTitle, endpoint } = widgetsConfigurations.currentSpi;

export const CurrentSPI = ({
    widgetId,
    localFilters,
    latestVersionId,
    globalFilterData,
    compareVersionId,
    contractId,
    externalComponents = null,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = defaultTitle,
}: IComponentProps) => {
    const { ui, api } = useCurrentSPIProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        contractId,
    });

    const { editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title,
        ui,
        reportWidgetRef,
        apiPayload: api.getCurrentSPI.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useCurrentSPILogic({ api });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                <div
                    className={`${classes.container} ${WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) ? classes.inEditMode : ''}`}
                >
                    <div className={`${classes.value} ${classes[logic.valueClassName]}`}>
                        {api.getCurrentSPI.instance.isFetching ? <OverlayWithSpinner /> : logic.calculatedValue}
                    </div>
                </div>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
